import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, Repeat2, Heart, Send, Eye, BarChart3, Loader2 } from "lucide-react";
import ImageView from "../ImageView";
import CanvaView from "../CanvaView";
import { getBaseUrl } from "../../../api/api";
import { getCurrentThreadsMe } from "../../../api/AppLinkApi";
import { timeAgoShort } from "../../helpers";

interface Props {
  postType: string;
  content: string;
  scheduledDateTime: Date;
  mediaId: string;
  mediaType: "HOST" | "CANVA";
  engagement: any;
  onToggleInsights?: () => void;
}

export default function ThreadsPost({
  postType,
  content,
  scheduledDateTime,
  mediaId,
  mediaType,
  engagement,
  onToggleInsights,
}: Props) {
  const { t } = useTranslation();

  const [displayName, setDisplayName] = useState(t("Anonymous"));
  const [avatarUrl, setAvatarUrl] = useState();
  const [threadsHandle, setThreadsHandle] = useState<string>("");
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfile = () => {
    getCurrentThreadsMe().then((data) => {
      setDisplayName(data?.name || t("Anonymous"));
      setAvatarUrl(data?.threads_profile_picture_url);
      // Get Threads handle/username from the profile
      const handle = data?.username || data?.threads_username || "";
      setThreadsHandle(handle ? `@${handle}` : "");
    });
  };

  useEffect(() => {
    // Reset loading state when mediaId or mediaType changes
    if (mediaId && postType === "image") {
      setImageLoading(true);
      setImageError(false);
    } else {
      setImageLoading(false);
    }
  }, [mediaId, mediaType, postType]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const totalCount = (data: any) => {
    return (data?.values || []).reduce((total, item: any) => total + (item?.value || 0), 0);
  };

  const metrics = engagement.getIn(["metrics", "data"])?.toJS() || [];
  const views = metrics.find((item) => item.name === "views") || {};
  const likes = metrics.find((item) => item.name === "likes") || {};
  const replies = metrics.find((item) => item.name === "replies") || {};
  const reposts = metrics.find((item) => item.name === "reposts") || {};
  const quotes = metrics.find((item) => item.name === "quotes") || {};

  return (
    <div className="max-w-md mx-auto border-b border-gray-200 pb-4 mb-4">
      {/* Header */}
      <div className="flex items-start space-x-3">
        {avatarUrl ? (
          <img src={avatarUrl} alt="avatar" className="w-10 h-10 rounded-full" />
        ) : (
          <div className="w-10 h-10 rounded-full">
            <svg className="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h3 className="text-gray-900 font-semibold">{displayName}</h3>
              <span className="pl-3 text-sm text-gray-500">{timeAgoShort(scheduledDateTime)}</span>
            </div>
            <button className="text-gray-600 text-xl">⋯</button>
          </div>
          {/* Thread by @handle - Required for Facebook App Review */}
          {threadsHandle && (
            <p className="text-xs text-gray-500 mt-1 font-medium">
              {t("Thread by")} {threadsHandle}
            </p>
          )}
          <p className="text-gray-700 mt-1">{content}</p>

          {/* Image (optional) */}
          {postType === "image" ? (
            <div
              className="mt-3 rounded-xl overflow-hidden border border-gray-200 relative bg-gray-100"
              style={{ minHeight: "200px", maxHeight: "600px" }}
            >
              {imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                  <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                </div>
              )}
              {imageError && !imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                  <div className="text-center text-gray-400">
                    <p className="text-sm">{t("Failed to load image")}</p>
                  </div>
                </div>
              )}
              {mediaId ? (
                <>
                  {mediaType === "CANVA" ? (
                    <div
                      className={`w-full ${
                        imageLoading ? "opacity-0" : "opacity-100 transition-opacity duration-300"
                      }`}
                    >
                      <CanvaView
                        fileId={mediaId}
                        className="w-full h-auto object-contain"
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                      />
                    </div>
                  ) : (
                    <div
                      className={`w-full ${
                        imageLoading ? "opacity-0" : "opacity-100 transition-opacity duration-300"
                      }`}
                    >
                      <ImageView
                        fileName={mediaId}
                        className="w-full h-auto object-contain"
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                      />
                    </div>
                  )}
                </>
              ) : (
                <img
                  src={`${getBaseUrl()}/static/images/upload-images.png`}
                  alt=""
                  className="w-full h-auto object-cover"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              )}
            </div>
          ) : null}

          {/* Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6 mt-3 text-gray-500">
              <button
                className="flex items-center space-x-1 hover:text-gray-900 transition"
                title={t("Like")}
              >
                <Heart className="w-5 h-5" />
                <span className="text-sm">{totalCount(likes)}</span>
              </button>
              <button
                className="flex items-center space-x-1 hover:text-gray-900 transition"
                title={t("Comment")}
              >
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm">{totalCount(replies)}</span>
              </button>
              <button
                className="flex items-center space-x-1 hover:text-gray-900 transition"
                title={t("Repost")}
              >
                <Repeat2 className="w-5 h-5" />
                <span className="text-sm">{totalCount(reposts)}</span>
              </button>
              <button
                className="flex items-center space-x-1 hover:text-gray-900 transition"
                title={t("Share")}
              >
                <Send className="w-5 h-5" />
                <span className="text-sm">{totalCount(quotes)}</span>
              </button>
            </div>
            <div className="flex items-center space-x-6 mt-3">
              <button
                className="flex items-center space-x-1 hover:text-gray-900 transition text-gray-500"
                title={t("View")}
              >
                <Eye className="w-5 h-5" />
                <span className="text-sm">{totalCount(views)}</span>
              </button>
              {onToggleInsights && (
                <button
                  onClick={onToggleInsights}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
                  title={t("View Insights")}
                >
                  <BarChart3 className="w-4 h-4" />
                  <span className="text-sm">{t("Insights")}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
